import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import cx from 'classnames';
import { Row, Col } from 'antd';
import { useDidUpdate } from 'rooks';

import BaseAlert from 'components/wrappers/BaseAlert';
import {
  signUp,
  sendConfirmationAgain,
} from 'redux/reducers/authenticationSlice';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import FormRegistration from 'components/FormRegistration';
import { RegistrationFormValues } from 'components/FormRegistration/FormRegistration';
import { useAppDispatch } from 'hooks/useAppDispatch';

import style from '../AuthorizationPage.module.less';

export default function SignUpPage() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [submittedData, setSubmittedData] =
    useState<RegistrationFormValues | null>(null);
  const [isDataPrefilled, setIsDataPrefilled] = useState(false);
  const [invitationId, setInvitationId] = useState<string>();
  const [initialValues, setInitialValues] = useState<RegistrationFormValues>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
  });

  useDidUpdate(() => {
    // Click on register link after thank you message
    // should display registration form.
    // https://mybasepay.atlassian.net/browse/MBOM-394
    if (location.pathname !== '/registration' || !submittedData) return;

    setSubmittedData(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    if (location.state?.prefilledData) {
      const { invitationId, prefilledData, ...other } = location.state;

      setInitialValues((v) => ({
        ...v,
        firstName: prefilledData.primaryContact?.firstName || '',
        lastName: prefilledData.primaryContact?.lastName || '',
        email: prefilledData.primaryContact?.email || '',
        password: '', // it returns null
      }));
      setInvitationId(invitationId);
      setIsDataPrefilled(true);

      navigate(`/registration${location.search}`, {
        replace: true,
        state: { ...other },
      });
    }
    // eslint-disable-next-line
  }, []);

  const handleSubmit = async (values: RegistrationFormValues) => {
    const result = await dispatch(
      signUp({ ...values, invitedPartnerId: invitationId || undefined }),
    );

    if (signUp.fulfilled.match(result)) {
      setSubmittedData(values);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const onSendAgain = async (event: any) => {
    if (!submittedData) return;

    event.preventDefault();

    const result = await dispatch(sendConfirmationAgain(submittedData.email));

    if (sendConfirmationAgain.fulfilled.match(result)) {
      BaseAlert.success('Confirmation email has been re-sent.');
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  return (
    <div className={cx('scroll-box', style.authPage)}>
      {!submittedData && (
        <Row justify="end" className={style.fullHeight}>
          <Col xl={14}>
            <div className={style.authPageBg} />
          </Col>
          <Col xs={24} xl={10}>
            <FormRegistration
              isEmailDisabled={isDataPrefilled}
              initialValues={initialValues}
              handleSubmit={handleSubmit}
            />
          </Col>
        </Row>
      )}

      {submittedData && (
        <div className={style.registerEnd}>
          <div className={style.registerEndBg}>
            <div className={cx(style.registerEndLogo, style.registerEndLeft)} />
            <div
              className={cx(style.registerEndLogo, style.registerEndRight)}
            />
            <div
              className={style.registerEndContent}
              data-id="register-success"
            >
              <span
                className={cx('icon icon-check-filled', style.registerIcon)}
              />

              <div className={style.registerEndTitle}>
                Thank you! <br />
                We are almost there.
              </div>

              <div className={style.registerEndMessage}>
                Please click the link sent to
                <div className={style.registerEndMail}>
                  <span>{submittedData.email}</span>
                </div>
                to confirm your registration.
              </div>
            </div>
          </div>

          <div className={style.registerEndContent}>
            <div className={style.registerEndText}>
              <div className={style.dInline}>Haven’t received it? &nbsp;</div>
              <div className={style.dInline}>
                Please check Spam or lets try sending again
              </div>
              <a
                href="/"
                className={style.registerEndLink}
                data-id="signUpSendAgainBtn"
                onClick={onSendAgain}
              >
                Send it again
              </a>
            </div>
            <div className={style.registerEndText}>
              Still having trouble? Email us:{' '}
              <a href="mailto:help@mybasepay.com">help@mybasepay.com</a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
